<template>
  <div v-if="show" class="col-12 col-xl-6">
    <location-user
      :items="form.owners"
      :disabled="ownersDisabled"
      :label="$t('form.labels.owner')"
      :country-code="countryCode"
      :key="`owners-${countryCode}`"
      :subscription="form.subscription"
      :location="form"
      user-role="owners"
      input-class="mb-0"
      @completed="$emit('completed')"
    />
    <location-user
      v-if="showTroncMastersBlock"
      :items="form.troncMasters"
      :disabled="troncMastersDisabled"
      :label="troncMasterLabel"
      :country-code="countryCode"
      :key="`troncs-${countryCode}`"
      :subscription="form.subscription"
      :location="form"
      user-role="troncMasters"
      input-class="my-0"
      @completed="$emit('completed')"
    />
    <location-user
      v-if="showAccountants"
      :items="form.accountants"
      :disabled="accountantsDisabled"
      :label="$t('form.labels.accountant')"
      :country-code="countryCode"
      :key="`accountants-${countryCode}`"
      :subscription="form.subscription"
      :location="form"
      user-role="accountants"
      input-class="my-0"
      @completed="$emit('completed')"
    />

    <div
      v-if="showSharedCardToggle"
      class="my-2"
    >
      <label
        class="font-14 text-grey"
        :class="{ 'disabled': disabled }"
      >
        Shared payout card
      </label>
      <ui-checkbox
        v-model="form.isAllowedSharedCard"
        class="mt-2"
        :disabled="disabled"
      >
        Allow tips to shared card
      </ui-checkbox>
    </div>

    <div
      v-if="showSharedBankAccountToggle"
      class="my-2"
    >
      <label
        class="font-14 text-grey"
        :class="{ 'disabled': disabled }"
      >
        Shared payout bank account
      </label>
      <ui-checkbox
        v-model="form.isAllowedSharedBankAccount"
        class="mt-2"
        :disabled="disabled"
      >
        Allow tips to shared bank account
      </ui-checkbox>
    </div>

    <template v-if="!form.isMasterLocation">
      <validation-provider
        v-if="!isNoPaymentGateway"
        tag="div"
        name="Pay buttons"
        rules="required"
        mode="passive"
        class="my-2 mt-5"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.payButtonValue"
          :items="payButtons"
          label="Pay buttons"
          :placeholder="$t('form.placeholders.select')"
          label-key="displayName"
          value-key="id"
          value-original
          filterable
          required
          :disabled="disabled"
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="isClientGateway"
        tag="div"
        :name="$t('form.labels.clientGatewayFixedFees', { currency: locationCurrency })"
        rules="required|float|min_value:0"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.clientGatewayFixedFees"
          :label="$t('form.labels.clientGatewayFixedFees', { currency: locationCurrency })"
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="isClientGateway"
        tag="div"
        :name="$t('form.labels.clientGatewayVariableFees')"
        rules="required|float|min_value:0"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-input
          v-model="form.clientGatewayVariableFees"
          :label="$t('form.labels.clientGatewayVariableFees')"
          required
          :error="errors[0]"
        />
      </validation-provider>

      <validation-provider
        v-if="form.isShowRyftAccount"
        tag="div"
        :name="$t('form.labels.ryftAccount')"
        v-slot="{ errors }"
      >
        <ui-input
          :value="form.paymentSystemUserId || 'No Ryft Company Account Added'"
          :label="$t('form.labels.ryftAccount')"
          :error="errors[0]"
          disabled
          :copy-button="form.paymentSystemUserId"
        />
      </validation-provider>

      <template v-if="showPayroll">
        <validation-provider
          tag="div"
          name="Payroll frequency"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.payrollFrequency"
            :items="payrollFrequencies"
            :placeholder="$t('form.placeholders.select')"
            label="Payroll frequency"
            label-key="label"
            value-key="value"
            filterable
            required
            :error="errors[0]"
          />
        </validation-provider>

        <validation-provider
          v-if="showPayrollDay"
          tag="div"
          name="Payroll day"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.payrollDay"
            :items="payrollDays"
            :placeholder="$t('form.placeholders.select')"
            label="Payroll day"
            label-key="label"
            value-key="value"
            filterable
            required
            :error="errors[0]"
          />
        </validation-provider>

        <validation-provider
          v-if="showPayrollCustomDate"
          tag="div"
          name="Payroll date"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-calendar-picker
            v-model="form.payrollCustomDate"
            placeholder="Payroll date"
            label="Payroll date"
            required
            :error="errors[0]"
          />
        </validation-provider>
      </template>
    </template>

    <ui-select
      v-model="form.salesPerson"
      :items="salesPeopleOptions"
      :placeholder="$t('form.placeholders.select')"
      :disabled="disabled"
      label="Sales Manager"
      label-key="fio"
      value-key="id"
      filterable
      value-original
    />

    <ui-select
      v-model="form.customerSuccessManager"
      :items="salesPeopleOptions"
      :placeholder="$t('form.placeholders.select')"
      :disabled="disabled"
      label="Customer Support Manager"
      label-key="fio"
      value-key="id"
      filterable
      value-original
    />

    <ui-input
      v-if="showStripeAccount"
      v-model="form.stripeAccountId"
      label="Stripe Account"
      :disabled="true"
    />

    <ui-input
      v-if="showCheckoutAccount"
      v-model="checkoutAccount"
      label="Checkout Account"
      :disabled="true"
    />

    <validation-provider
      v-if="showDistributionSchemes"
      tag="div"
      name="Options for distribution schemes"
      rules="required"
      mode="passive"
      class="mb-2 mt-5"
      v-slot="{ errors }"
    >
      <ui-select
        v-model="form.distributionSchemes"
        :items="distributionSchemes"
        label="Options for distribution schemes"
        :placeholder="$t('form.placeholders.select')"
        optional-key="text"
        label-key="shortText"
        value-key="id"
        value-original
        small-dropdown
        filterable
        required
        :disabled="disabled"
        :error="errors[0]"
      />
    </validation-provider>

    <validation-provider
      tag="div"
      :name="$t('form.labels.posModel')"
      rules="required|max:128"
      mode="passive"
      v-slot="{ errors }"
    >
      <ui-input
        v-model="form.posModel"
        :label="$t('form.labels.posModel')"
        maxlength="128"
        required
        :error="errors[0]"
      />
    </validation-provider>

    <div v-if="showStaffTipOuts">
      <validation-provider
        tag="div"
        :name="$t('form.labels.staffTipOuts')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.isTipOutEnabled"
          :items="onOffSelectorItems"
          :label="$t('form.labels.staffTipOuts')"
          :label-info="$t('staffTipOuts.tooltip')"
          :placeholder="$t('form.placeholders.select')"
          label-key="label"
          value-key="value"
          required
          :error="errors[0]"
        />
      </validation-provider>
    </div>

    <div v-if="showHouseTipOuts">
      <validation-provider
        tag="div"
        :name="$t('form.labels.houseTipOuts')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.isHouseTipOuts"
          :items="onOffSelectorItems"
          :label="$t('form.labels.houseTipOuts')"
          :placeholder="$t('form.placeholders.select')"
          :bottom-info="$t('houseTipOuts.helper')"
          class="mb-5"
          label-key="label"
          value-key="value"
          required
          :error="errors[0]"
          @input="handleHouseTipOutsChange"
        />
      </validation-provider>

      <template v-if="showHouseTipOutsDetails">
        <ui-input
          :value="displayHouseTipOutsName"
          :label="$t('houseTipOuts.nameInReports')"
          readonly
          class="mb-4 house-tip-outs-name"
        >
          <template #addonRight>
            <ui-icon-button
              name="pencil"
              size="20"
              class="text-primary"
              :title="$t('houseTipOuts.changeName')"
              @click.prevent="showHouseTipOutsRenameModal = true"
            />
          </template>
        </ui-input>

        <ui-switcher
          v-model="form.enableHouseTipOutsForce"
          :label="$t('form.labels.enableHouseTipOutsForce')"
          class="mb-2"
        />
        <p class="text-grey font-12 mb-5">
          {{ houseTipOutsLimitHelp }}
        </p>
      </template>

      <location-house-tip-outs-rename-modal
        v-if="showHouseTipOutsRenameModal"
        v-model="showHouseTipOutsRenameModal"
        :current-name="displayHouseTipOutsName"
        @applied="handleHouseTipOutsNameApplied"
      />
    </div>

    <template v-if="showCutOff">
      <validation-provider
        tag="div"
        :name="$t('form.labels.cutOffSchedule')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.cutOffSchedule"
          :items="cutOffScheduleOptions"
          :placeholder="$t('form.placeholders.select')"
          :label="$t('form.labels.cutOffSchedule')"
          :label-info="$t('cutOff.scheduleTooltip')"
          label-key="label"
          value-key="value"
          filterable
          required
          :error="errors[0]"
          :disabled="disabled"
        />
      </validation-provider>

      <validation-provider
        v-if="isSpecificDaySchedule"
        tag="div"
        :name="$t('form.labels.cutOffDay')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.cutOffDay"
          :items="cutOffDayOptions"
          :placeholder="$t('form.placeholders.select')"
          :label="$t('form.labels.cutOffDay')"
          :label-info="$t('cutOff.dayTooltip')"
          label-key="label"
          value-key="value"
          filterable
          required
          :error="errors[0]"
          :disabled="disabled"
        />
      </validation-provider>

      <div
        v-if="isTwicePerMonthSchedule"
        class="d-flex"
      >
        <validation-provider
          tag="div"
          class="flex-fill pr-2"
          :name="$t('form.labels.firstCutOffDay')"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.firstCutOffDay"
            :items="firstCutOffDayOptions"
            :placeholder="$t('form.placeholders.select')"
            :label="$t('form.labels.firstCutOffDay')"
            class="my-2"
            label-key="label"
            value-key="value"
            filterable
            required
            :error="errors[0]"
            :disabled="disabled"
          />
        </validation-provider>

        <validation-provider
          tag="div"
          class="flex-fill pl-2"
          :name="$t('form.labels.secondCutOffDate')"
          rules="required"
          mode="passive"
          v-slot="{ errors }"
        >
          <ui-select
            v-model="form.secondCutOffDate"
            :items="secondCutOffDateOptions"
            :placeholder="$t('form.placeholders.select')"
            :label="$t('form.labels.secondCutOffDate')"
            :label-info="$t('cutOff.daysTooltip')"
            class="my-2"
            label-key="label"
            value-key="value"
            filterable
            required
            :error="errors[0]"
            :disabled="disabled"
          />
        </validation-provider>
      </div>
    </template>

    <div v-if="form.isTipOutEnabled">
      <validation-provider
        tag="div"
        :name="$t('form.labels.isDetailedTransactionBreakdown')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.isDetailedTransactionBreakdown"
          :items="onOffSelectorItems"
          :label="$t('form.labels.isDetailedTransactionBreakdown')"
          :placeholder="$t('form.placeholders.select')"
          label-key="label"
          value-key="value"
          required
          :error="errors[0]"
        />
      </validation-provider>
    </div>

    <div v-if="form.isTipOutEnabled">
      <validation-provider
        tag="div"
        :name="$t('form.labels.isContributorsTeamDistribution')"
        rules="required"
        mode="passive"
        v-slot="{ errors }"
      >
        <ui-select
          v-model="form.isContributorsTeamDistribution"
          :items="onOffSelectorItems"
          :label="$t('form.labels.isContributorsTeamDistribution')"
          :placeholder="$t('form.placeholders.select')"
          label-key="label"
          value-key="value"
          required
          :error="errors[0]"
        />
      </validation-provider>
    </div>

    <div
      v-if="status === 'NOT_ACTIVE' && turnOffDate && removeReason"
      class="col-12 pl-0 mb-4 col-md-auto mt-2 mt-md-0 d-flex
      font-16 text-grey flex-column align-items-start justify-content-around"
    >
      Pay turned off on {{ turnOffDate | date }}<br>
      {{ removeReason }}
    </div>

    <div
      v-if="status === 'ACTIVE' && onboardingDate"
      class="col-12 pl-0 mb-4 col-md-auto mt-2 mt-md-0 d-flex
      flex-column align-items-start justify-content-around"
    >
      <span class="font-16 text-grey">
        Onboarded on {{ onboardingDate | date }}
      </span>
      <ui-button
        link
        type="primary"
        :disabled="isOnboardingLoading"
        @click.prevent="$emit('activate')"
      >
        {{ 'Edit date' }}
      </ui-button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { capitalize, range, map } from 'lodash';
import getOrdinalSuffix from '@/utils/getOrdinalSuffix';
import getCurrency from '@/utils/getCurrency';
import {
  CUT_OFF_SCHEDULE,
  CUT_OFF_SECONDARY,
  CUT_OFF_DAY_MAX,
} from '@/config/cutOffSchedule';
import { LABELS } from '@/config/currencies';
import maxTipsAmount from '@/config/maxTipsAmount';
import { CASE_TYPE } from '@/config/caseType';
import { PAYMENT_SYSTEM } from '@/config/paymentSystems';
import { PROCESSING_METHOD } from '@/config/processingMethod';
import LocationUser from './LocationUser.vue';
import LocationHouseTipOutsRenameModal from './LocationHouseTipOutsRenameModal.vue';

export default {

  components: {
    LocationUser,
    LocationHouseTipOutsRenameModal,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    isOnboardingLoading: Boolean,
    disabled: Boolean,
  },

  data: () => ({
    payrollFrequencies: [
      {
        value: 'NOT_AVAILABLE',
        label: 'N/A',
      },
      {
        value: 'FORTNIGHTLY',
        label: 'Fortnightly',
      },
      {
        value: 'MONTHLY',
        label: 'Monthly',
      },
    ],
    payrollDays: [
      {
        value: 'LAST_FRIDAY',
        label: 'Last friday of the month',
      },
      {
        value: 'LAST_BUSINESS_DAY',
        label: 'Last business day of the month',
      },
      {
        value: 'OTHER',
        label: 'Other',
      },
    ],
    onOffSelectorItems: [
      {
        label: 'On',
        value: true,
      },
      {
        label: 'Off',
        value: false,
      },
    ],
    showHouseTipOutsRenameModal: false,
  }),

  computed: {
    ...mapState('settings', [
      'payButtons',
      'salesPeople',
      'distributionSchemes',
    ]),
    ...mapGetters({
      isAdmin: 'auth/isAdmin',
      isSalesPerson: 'auth/isSalesPerson',
      isSupportAdmin: 'auth/isSupportAdmin',
    }),
    showCutOff() {
      return (this.isCase5 || this.isCase2)
        && this.form.isMarketPlaceEnabled;
    },
    isSpecificDaySchedule() {
      return this.form.cutOffSchedule === CUT_OFF_SCHEDULE.SPECIFIC_DAY_EACH_MONTH;
    },
    isTwicePerMonthSchedule() {
      return this.form.cutOffSchedule === CUT_OFF_SCHEDULE.TWICE_PER_MONTH;
    },
    cutOffScheduleOptions() {
      return [
        {
          value: CUT_OFF_SCHEDULE.SPECIFIC_DAY_EACH_MONTH,
          label: this.$t('form.labels.specificDayEachMonth'),
        },
        {
          value: CUT_OFF_SCHEDULE.TWICE_PER_MONTH,
          label: this.$t('form.labels.twicePerMonth'),
        },
      ];
    },
    cutOffDayOptions() {
      return map(range(1, CUT_OFF_DAY_MAX + 1), (day) => ({
        value: day,
        label: `${getOrdinalSuffix(day)} ${this.$t('form.labels.ofEveryMonth')}`,
      }));
    },
    firstCutOffDayOptions() {
      return map(range(1, CUT_OFF_DAY_MAX + 1), (day) => ({
        value: day,
        label: getOrdinalSuffix(day),
      }));
    },
    secondCutOffDateOptions() {
      const firstDay = this.form.firstCutOffDay;
      const days = firstDay
        ? map(range(firstDay + 1, CUT_OFF_DAY_MAX + 1), (day) => ({
          value: String(day),
          label: getOrdinalSuffix(day),
        }))
        : [];

      return [
        ...days,
        {
          value: CUT_OFF_SECONDARY.LAST_DAY,
          label: this.$t('form.labels.lastDay'),
        },
        {
          value: CUT_OFF_SECONDARY.LAST_WORKING_DAY,
          label: this.$t('form.labels.lastWorkingDay'),
        },
      ];
    },
    canManageHouseTipOuts() {
      return this.isAdmin || this.isSalesPerson || this.isSupportAdmin;
    },
    showHouseTipOutsDetails() {
      return this.form.isHouseTipOuts && this.canManageHouseTipOuts;
    },
    displayHouseTipOutsName() {
      return String(this.form.houseTipOutsName ?? '').trim() || 'House';
    },
    houseTipOutsLimitHelp() {
      return this.form.enableHouseTipOutsForce
        ? this.$t('houseTipOuts.limitHelpOn')
        : this.$t('houseTipOuts.limitHelpOff');
    },
    isValidUsAndDeCaseType() {
      const isValidCaseType = this.isCase1 || this.isCase2 || this.isCase4;

      return (this.isUSA || this.isGermany || this.isDenmark) && isValidCaseType;
    },
    troncMasterLabel() {
      if (this.isValidUsAndDeCaseType) {
        return this.$t('form.labels.administrator');
      }

      return this.$t('form.labels.troncMaster', this.form.country);
    },
    showTroncMastersBlock() {
      return this.isUk || this.isUAE
        || this.isValidUsAndDeCaseType;
    },
    locationCurrency() {
      // Countries may allow more than one currency, so the selected one wins over the country default.
      return LABELS[this.form.currency] || getCurrency(this.form.country).label;
    },
    salesPeopleOptions() {
      return [
        {
          id: null,
          fio: 'N/A',
        },
        ...this.salesPeople,
      ];
    },
    countryCode() {
      return this.form.country.toUpperCase();
    },
    // Payout plan fees are currency-based, so their example tooltip follows the selected currency.
    currencyCode() {
      return (this.form.currency || getCurrency(this.form.country).iso).toUpperCase();
    },
    isCase1() {
      return this.form.caseType === CASE_TYPE.CASE_1;
    },
    isCase2() {
      return this.form.caseType === CASE_TYPE.CASE_2;
    },
    isCase3() {
      return this.form.caseType === CASE_TYPE.CASE_3;
    },
    isCase4() {
      return this.form.caseType === CASE_TYPE.CASE_4;
    },
    isCase5() {
      return this.form.caseType === CASE_TYPE.CASE_5;
    },
    isTipPayoutIndividual() {
      return this.form.tipPayoutType === 'INDIVIDUAL';
    },
    isTipPayoutEmployer() {
      return this.form.tipPayoutType === 'EMPLOYER';
    },
    isCommon() {
      return this.form.tipsType === 'COMMON'
        || this.form.tipsType === 'COMMON_PRIORITY';
    },
    isMixed() {
      return this.form.tipsType === 'MIXED';
    },
    ownersDisabled() {
      return this.disabled
        || this.form.isMasterLocation;
    },
    troncMastersDisabled() {
      return !(this.isCommon || this.isMixed)
        || this.isTipPayoutEmployer
        || this.disabled
        || this.form.caseType === CASE_TYPE.CASE_3;
    },
    accountantsDisabled() {
      return this.form.isMasterLocation
        || this.isNoPaymentGateway
        || this.disabled;
    },
    isGermany() {
      return this.form.country === 'de';
    },
    isDenmark() {
      return this.form.country === 'dk';
    },
    isUAE() {
      return this.form.country === 'ae';
    },
    isUk() {
      return this.form.country === 'gb';
    },
    isSwitzerland() {
      return this.form.country === 'ch';
    },
    isCanada() {
      return this.form.country === 'ca';
    },
    isSaudiArabia() {
      return this.form.country === 'sa';
    },
    isUSA() {
      return this.form.country === 'us';
    },
    show() {
      return !((this.isGermany || this.isDenmark || this.isUAE || this.isUSA) && this.disabled);
    },
    showAccountants() {
      return !this.isGermany
        && !this.isDenmark
        && !this.isUAE
        && !this.isCanada
        && !this.isSaudiArabia
        && !this.isUSA;
    },
    showSharedCardToggle() {
      return this.form.paymentSystem === PAYMENT_SYSTEM.CHECKOUT_UK_DASHBOARD
        && this.isUAE;
    },
    showSharedBankAccountToggle() {
      return this.form.paymentSystem === PAYMENT_SYSTEM.PAYER_MAX
        && this.isUAE;
    },
    isSelfReporting() {
      return this.form.taxReportingType === 'SELF_REPORTING';
    },
    isPayerMax() {
      return this.form.paymentSystem === PAYMENT_SYSTEM.PAYER_MAX;
    },
    isClientGateway() {
      return this.form.processingMethod === PROCESSING_METHOD.CLIENT_GATEWAY;
    },
    isNoPaymentGateway() {
      return this.form.processingMethod === PROCESSING_METHOD.NO_PAYMENT_GATEWAY;
    },
    showDistributionSchemes() {
      return (this.isCommon || this.isMixed)
        && !this.isTipPayoutEmployer
        && this.form.caseType !== CASE_TYPE.CASE_3;
    },
    showPayroll() {
      return (this.isUk || this.isSwitzerland)
        && !(this.isUk && this.isSelfReporting)
        && !this.form.caseType;
    },
    showPayrollDay() {
      return this.form.payrollFrequency
          && this.form.payrollFrequency !== 'NOT_AVAILABLE';
    },
    showPayrollCustomDate() {
      return this.form.payrollDay === 'OTHER';
    },
    isPersonal() {
      return this.form.tipsType === 'PERSONAL';
    },
    showStripeAccount() {
      return this.form.showStripeAccount
        && this.form.paymentSystem === PAYMENT_SYSTEM.STRIPE;
    },
    showCheckoutAccount() {
      return this.form.checkoutCard?.suffix
        && this.form.paymentSystem === PAYMENT_SYSTEM.CHECKOUT;
    },
    checkoutAccount() {
      const suffix = this.form.checkoutCard?.suffix;

      return suffix
        ? `.... ${suffix}`
        : '';
    },
    status() {
      return this.data?.status || '';
    },
    onboardingDate() {
      return this.data?.onboardingDate || '';
    },
    turnOffDate() {
      return this.data?.turnOffDate || '';
    },
    removeReason() {
      return this.data?.removeReason
        ? capitalize(this.data?.removeReason.split('_').join(' '))
        : '';
    },
    showStaffTipOuts() {
      return (this.isCommon || this.isMixed)
        && (this.isCase1 || this.isCase2);
    },
    showHouseTipOuts() {
      return this.isCommon && !this.isCase3;
    },
  },

  methods: {
    clearCutOffFields() {
      this.form.cutOffSchedule = null;
      this.form.cutOffDay = null;
      this.form.firstCutOffDay = null;
      this.form.secondCutOffDate = null;
    },
    handleHouseTipOutsChange(value) {
      this.form.enableHouseTipOutsForce = value;
    },
    setDefaultMaxTipsAmount() {
      this.form.maxTipsAmount = maxTipsAmount?.[this.form.currency]?.[this.form.type] || '';
    },
    handleHouseTipOutsNameApplied(name) {
      this.form.houseTipOutsName = name;
    },
  },

  watch: {
    showCutOff: {
      handler(show) {
        if (!show) {
          this.clearCutOffFields();
        }
      },
    },
    'form.cutOffSchedule': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.form.cutOffDay = null;
          this.form.firstCutOffDay = null;
          this.form.secondCutOffDate = null;
        }
      },
    },
    'form.firstCutOffDay': {
      handler(newValue, oldValue) {
        if (newValue === oldValue) {
          return;
        }

        const second = this.form.secondCutOffDate;
        const isStillValid = this.secondCutOffDateOptions
          .some(({ value }) => value === second);

        if (!isStillValid) {
          this.form.secondCutOffDate = null;
        }
      },
    },
    'form.platformFeeType': {
      handler() {
        this.form.feeSplitPercentage = 0;
      },
    },
    payButtons: {
      handler() {
        if (!this.form.id) {
          this.form.payButtonValue = null;
        }
      },
    },
    'form.payButtonValue': {
      handler(value) {
        const isButtonAmountExceeds = value.amountList.some((item) => item > this.form.maxTipsAmount);

        if (isButtonAmountExceeds) {
          this.$alert({
            title: this.$t('payButtonAmountModal.title'),
            message: this.$t('payButtonAmountModal.description'),
            applyButton: this.$t('form.buttons.ok'),
          });
        }
      },
    },
    'form.payrollFrequency': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.form.payrollDay = null;
        }
      },
    },
    'form.payrollDay': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.form.payrollCustomDate = null;
        }
      },
    },
    'form.tipsType': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.form.payoutPlanSetup = null;
          this.form.commonPayoutPlanSetup = null;
        }
      },
    },
    'form.currency': {
      handler() {
        this.setDefaultMaxTipsAmount();
      },
    },
    'form.type': {
      handler() {
        this.setDefaultMaxTipsAmount();
      },
    },
    'form.caseType': {
      handler(caseType) {
        if (caseType === CASE_TYPE.CASE_5) {
          this.form.payButtonValue = null;
          this.form.maxTipsAmount = null;
        }

        if (caseType === CASE_TYPE.CASE_3) {
          this.form.distributionSchemes = null;
        }

        if (caseType) {
            this.form.payrollFrequency = null;
            this.form.payrollDay = null;
            this.form.payrollCustomDate = null;
        }
      },
    },
    'form.paymentSystem': {
      handler(paymentSystem) {
        if (paymentSystem === PAYMENT_SYSTEM.CHECKOUT_UK_DASHBOARD) {
          this.form.isAllowedSharedBankAccount = false;
        }

        if (paymentSystem === PAYMENT_SYSTEM.PAYER_MAX) {
          this.form.isAllowedSharedCard = false;
        }
      },
    },
    'form.isTipOutEnabled': {
      handler() {
        this.form.isDetailedTransactionBreakdown = null;
        this.form.isContributorsTeamDistribution = null;
      },
    },
  },
};
</script>

<style>
.house-tip-outs-name {
  .form-group__body,
  .form-control {
    width: 100% !important;
  }
}
</style>
